import React, { useState, useEffect, ChangeEvent, KeyboardEvent } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Box, InputBase, IconButton, Divider, useMediaQuery } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { LocationOn } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useIsMobile } from "../../util/utils";

const HeaderSearch: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobile = useIsMobile();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [locationQuery, setLocationQuery] = useState<string>("");
  const [date, setDate] = useState<string>("");

  // Extract URL parameters on mount or when location.search changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    const loc = params.get("location");
    const d = params.get("date");
    if (q) setSearchQuery(q);
    if (loc) setLocationQuery(loc);
    if (d) setDate(d);
  }, [location.search]);

  // Handler to initiate search
  const handleSearch = () => {
    // You already redirected to /search from the previous page.
    // This is just to allow the user to update the query on the search page.
    // You can call navigate here if you want to update the URL.
    const queryParams = new URLSearchParams();
    if (searchQuery) queryParams.append("q", searchQuery);
    if (locationQuery) queryParams.append("location", locationQuery);
    if (date) queryParams.append("date", date);
    navigate(`/search?${queryParams.toString()}`);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  return (
    <Box display="flex" justifyContent="center" width="100%">
    <Box
      display="flex"
      flexDirection={{ xs: "column", sm: "row" }}
      alignItems="stretch"
      gap="0.5rem"
      width={isMobile ? "90%" : "100%"}
      maxWidth="800px"
    >
      {/* Search Field */}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSearch();
        }}
        display="flex"
        alignItems="center"
        sx={{
          backgroundColor: "white",
          borderRadius: "10px",
          padding: "0.5rem",
          minWidth: "250px",
          flexGrow: 1,
        }}
      >
        <SearchIcon style={{ color: "black" }} />
        <InputBase
          placeholder="Search for services, events, classes, or businesses"
          inputProps={{ "aria-label": "search" }}
          sx={{ marginLeft: "10px", color: "black", flexGrow: 1 }}
          value={searchQuery}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setSearchQuery(e.target.value)
          }
          onKeyDown={handleKeyDown}
        />
      </Box>
  
      {/* Location Field + Search Button as a group */}
      <Box
        display="flex"
        alignItems="center"
        gap="0.5rem"
        sx={{
          flexGrow: 1,
          flexDirection: "row",
          backgroundColor: "transparent",
        }}
      >
        {/* Location Input */}
        <Box
          display="flex"
          alignItems="center"
          sx={{
            backgroundColor: "white",
            borderRadius: "10px",
            padding: ".5rem",
            flexGrow: 1,
            minWidth: "150px",
          }}
        >
          <LocationOn style={{ color: "black" }} />
          <InputBase
            placeholder="Where?"
            inputProps={{ "aria-label": "location" }}
            sx={{ marginLeft: "10px", color: "black", flexGrow: 1 }}
            value={locationQuery}
            onChange={(e: ChangeEvent<HTMLInputElement>) =>
              setLocationQuery(e.target.value)
            }
            onKeyDown={handleKeyDown}
          />
        </Box>
  
        {/* Search Button (to the right of location) */}
        <IconButton
          onClick={handleSearch}
          sx={{
            backgroundColor: "#1976d2",
            color: "white",
            borderRadius: "10px",
            px: 2,
            height: "100%",
            '&:hover': {
              backgroundColor: "#115293",
            },
          }}
        >
          <SearchIcon />
        </IconButton>
      </Box>
    </Box>
  </Box>
  
  );
};

export default HeaderSearch;
